import { useState, useEffect } from 'react';
import {
  fetchAppPublishedStatus,
  getIsAppPublished,
  getIsAndroidPublished,
  setAppPublishedStatus,
  setAndroidPublishedStatus,
} from '../../config/appStore';

export function usePublicationControls(
  isAuthorized: boolean,
  logAdminAction: (action: string, details?: any) => Promise<void>,
  setNotification: (n: { message: string; type: 'error' | 'success' | 'warning' } | null) => void
) {
  const [appPublished, setAppPublished] = useState(false);
  const [appPublishedLoading, setAppPublishedLoading] = useState(true);
  const [appPublishedConfirm, setAppPublishedConfirm] = useState(false);

  const [androidPublished, setAndroidPublished] = useState(false);
  const [androidPublishedLoading, setAndroidPublishedLoading] = useState(true);
  const [androidPublishedConfirm, setAndroidPublishedConfirm] = useState(false);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchAppPublishedStatus().then(() => {
      // fetchAppPublishedStatus resolves with the Apple flag but populates both
      // module-level flags; read each via its own getter-equivalent here.
      setAppPublished(getIsAppPublished());
      setAppPublishedLoading(false);
      setAndroidPublished(getIsAndroidPublished());
      setAndroidPublishedLoading(false);
    });
  }, [isAuthorized]);

  const handleToggleAppPublished = async () => {
    setAppPublishedConfirm(false);
    const newValue = !appPublished;
    try {
      await setAppPublishedStatus(newValue);
      setAppPublished(newValue);
      await logAdminAction(newValue ? 'app_store_published' : 'app_store_unpublished', { published: newValue });
      setNotification({ message: newValue ? 'App Store links are now LIVE' : 'App Store links switched to Coming Soon mode', type: 'success' });
    } catch (err: any) {
      setNotification({ message: `Failed to update: ${err.message}`, type: 'error' });
    }
  };

  const handleToggleAndroidPublished = async () => {
    setAndroidPublishedConfirm(false);
    const newValue = !androidPublished;
    try {
      await setAndroidPublishedStatus(newValue);
      setAndroidPublished(newValue);
      await logAdminAction(newValue ? 'google_play_published' : 'google_play_unpublished', { published: newValue });
      setNotification({
        message: newValue
          ? 'Google Play links are now LIVE'
          : 'Google Play links switched to Coming Soon mode',
        type: 'success',
      });
    } catch (err: any) {
      setNotification({ message: `Failed to update: ${err.message}`, type: 'error' });
    }
  };

  return {
    appPublished,
    appPublishedLoading,
    appPublishedConfirm,
    setAppPublishedConfirm,
    androidPublished,
    androidPublishedLoading,
    androidPublishedConfirm,
    setAndroidPublishedConfirm,
    handleToggleAppPublished,
    handleToggleAndroidPublished,
  };
}
